import { BOOKING_STATUSES, FUEL_TYPES, TRANSMISSIONS, VEHICLE_CLASSES, VEHICLE_STATUSES, } from '@zuruflex/types';
import { z } from 'zod';
/**
 * Request validation shared by the API routes, the admin forms and the
 * concierge flow. One definition means the WhatsApp bot cannot construct a
 * booking the API would reject.
 */
export const e164Schema = z
    .string()
    .regex(/^\+\d{8,15}$/, 'Phone must be in E.164 format, e.g. +233201234567');
export const paginationSchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(100).default(20),
});
export const vehicleQuerySchema = paginationSchema.extend({
    q: z.string().trim().max(120).optional(),
    vehicleClass: z.enum(VEHICLE_CLASSES).optional(),
    transmission: z.enum(TRANSMISSIONS).optional(),
    fuelType: z.enum(FUEL_TYPES).optional(),
    status: z.enum(VEHICLE_STATUSES).optional(),
    minSeats: z.coerce.number().int().min(1).max(60).optional(),
    maxDailyRate: z.coerce.number().int().min(0).optional(),
    locationId: z.string().uuid().optional(),
    sort: z.enum(['relevance', 'price_asc', 'price_desc', 'newest']).default('relevance'),
});
export const createVehicleSchema = z.object({
    make: z.string().min(1).max(60),
    model: z.string().min(1).max(60),
    year: z.number().int().min(1980).max(new Date().getFullYear() + 1),
    plateNumber: z.string().min(3).max(20),
    vin: z.string().length(17).optional(),
    vehicleClass: z.enum(VEHICLE_CLASSES),
    transmission: z.enum(TRANSMISSIONS),
    fuelType: z.enum(FUEL_TYPES),
    seats: z.number().int().min(1).max(60),
    doors: z.number().int().min(1).max(8).default(4),
    colour: z.string().max(30).optional(),
    dailyRate: z.number().int().min(0),
    currency: z.string().length(3).default('GHS'),
    dailyKmAllowance: z.number().int().min(0).nullable().default(null),
    excessKmRate: z.number().int().min(0).default(0),
    depositAmount: z.number().int().min(0).default(0),
    features: z.array(z.string().max(60)).max(30).default([]),
    description: z.string().max(2000).optional(),
    locationId: z.string().uuid(),
});
export const updateVehicleSchema = createVehicleSchema.partial().extend({
    status: z.enum(VEHICLE_STATUSES).optional(),
    odometerKm: z.number().int().min(0).optional(),
});
/** A booking window must be in the future, at least an hour, at most a year. */
export const bookingWindowSchema = z
    .object({
    pickupAt: z.coerce.date(),
    dropoffAt: z.coerce.date(),
})
    .refine((v) => v.dropoffAt > v.pickupAt, {
    message: 'Drop-off must be after pick-up',
    path: ['dropoffAt'],
})
    .refine((v) => v.dropoffAt.getTime() - v.pickupAt.getTime() >= 3_600_000, {
    message: 'Minimum hire is one hour',
    path: ['dropoffAt'],
})
    .refine((v) => v.dropoffAt.getTime() - v.pickupAt.getTime() <= 365 * 86_400_000, {
    message: 'Maximum hire is one year',
    path: ['dropoffAt'],
});
export const createBookingSchema = z
    .object({
    vehicleId: z.string().uuid(),
    customerId: z.string().uuid().optional(),
    customerPhone: e164Schema.optional(),
    customerName: z.string().min(2).max(120).optional(),
    pickupLocationId: z.string().uuid(),
    dropoffLocationId: z.string().uuid().optional(),
    pickupAt: z.coerce.date(),
    dropoffAt: z.coerce.date(),
    notes: z.string().max(1000).optional(),
})
    .refine((v) => v.customerId ?? v.customerPhone, {
    message: 'Either customerId or customerPhone is required',
    path: ['customerPhone'],
})
    .refine((v) => v.dropoffAt > v.pickupAt, {
    message: 'Drop-off must be after pick-up',
    path: ['dropoffAt'],
});
export const updateBookingStatusSchema = z.object({
    status: z.enum(BOOKING_STATUSES),
    reason: z.string().max(500).optional(),
});
export const quoteRequestSchema = z.object({
    vehicleId: z.string().uuid(),
    pickupAt: z.coerce.date(),
    dropoffAt: z.coerce.date(),
});
